import { useMemo } from 'react';

import { useQuery, gql, useApolloClient } from '@apollo/client';
import { css } from '@emotion/react';
import { Skeleton, Typography, Stack, Badge } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation, Types, RouteLink } from '@scorenco/core';

import { ClubLogo, Score } from '../../components';
import { Image } from '../../primitives';
import { T_KEYS } from '../../translations';

import { Card } from './_internal';
import { GetEventForEventHeroDocument } from './EventHero.query.generated';

const normalizeLive = (live?: Types.Lives_Live) => {
  if (!live) {
    return {
      halfTime: undefined,
    };
  }

  return (live.posts || ([] as Types.Lives_Post[])).reduce(
    (acc, post) => {
      if (post.action?.action_type === 'half' && post.data?.end) {
        return {
          ...acc,
          halfTime: post,
        };
      }

      return acc;
    },
    {
      halfTime: undefined as Types.Lives_Post | undefined,
    }
  );
};

const TeamName = ({ name, ...rest }: { name: string }) => (
  <Typography
    css={css`
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      flex: 1;
      overflow: hidden;
      line-height: 1.2;
      text-transform: uppercase;
      font-size: 0.8rem;
    `}
    variant="h5"
    {...rest}
  >
    {name || <Skeleton />}
  </Typography>
);

const EventStatus = ({ status, ...rest }: { status?: string }) => {
  const { t } = useTranslation();
  if (status === 'finish' || status === 'postponed') {
    return (
      <Typography {...rest} sx={{ color: 'white' }}>
        {status === 'finish' && t(T_KEYS.EVENT_FINISH)}
        {status === 'postponed' && t(T_KEYS.EVENT_POSTPONED)}
      </Typography>
    );
  } else {
    return null;
  }
};

export const EventHero = ({
  event,
  live,
  ...rest
}: {
  event?: Types.Competitions_Event_Detail;
  live?: Types.Lives_Live;
}) => {
  const { t } = useTranslation();
  const eventQuery = useQuery(GetEventForEventHeroDocument, {
    variables: {
      eventId: event?.id as number,
    },
    context: { role: 'anonymous' },
    skip: !event?.id,
  });

  // On vérifie si le match est la démo en vérifiant le cache
  const client = useApolloClient();
  const demo = client.readFragment({
    id: `competitions_event:${event?.id}`,
    fragment: gql`
      fragment Event on competitions_event {
        id
        demo
      }
    `,
  })?.demo;

  const e = eventQuery.data?.competitions_event_detail_by_id?.[0] || event;
  const l = useMemo(() => normalizeLive(live), [live]);
  const isStarted =
    event?.teams?.reduce(
      (acc: boolean, team: any) =>
        acc && team?.score !== null && team?.score !== undefined,
      true
    ) ?? false;

  let scoreboard;
  if (e?.status === 'to_come') {
    scoreboard = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.2rem',
            fontWeight: 800,
            lineHeight: 1,
            textAlign: 'center',
          }}
          variant="body1"
          component="span"
        >
          {(e?.date &&
            new Date(e.date).toLocaleDateString([], {
              month: 'short',
              day: '2-digit',
            })) || <Skeleton />}
        </Typography>
        <Typography
          variant="body1"
          component="span"
          sx={{ textAlign: 'center' }}
        >
          {(e?.time &&
            new Date(e.time).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })) || <Skeleton />}
        </Typography>
      </Box>
    );
  } else if (e?.status === 'live' && !isStarted) {
    scoreboard = (
      <Typography
        variant="body1"
        sx={{
          fontStyle: 'italic',
          textAlign: 'center',
          lineHeight: 1.2,
          color: 'white',
        }}
        component="span"
        dangerouslySetInnerHTML={{ __html: t(T_KEYS.EVENT_CARD_GIVE_SCORE) }}
      />
    );
  } else {
    scoreboard = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              marginRight: '0.75rem',
            }}
          >
            {e?.teams[0]?.data?.sets?.map((_, index) => {
              return (
                <Score
                  key={index}
                  score={e?.teams[0]?.data?.sets[index]}
                  opponentScore={e?.teams[1]?.data?.sets[index]}
                  isWinner={
                    e?.teams[0]?.data?.sets[index] >
                    e?.teams[1]?.data?.sets[index]
                  }
                  sx={{ width: '1.1rem', height: '1.5rem', mb: 1 }}
                />
              );
            })}
          </Box>

          <Score
            score={e?.teams?.[0]?.score}
            opponentScore={e?.teams?.[1]?.score}
            isWinner={
              e?.status === 'finish' && e?.teams[0]?.score > e?.teams[1]?.score
            }
            className="event-hero-scores"
            sx={{
              width: '5rem',
              height: '4rem',
              mr: '0.25rem',
              fontSize: '2.5rem',
            }}
          />
          <Score
            score={e?.teams?.[1]?.score}
            opponentScore={e?.teams?.[0]?.score}
            isWinner={
              e?.status === 'finish' && e?.teams[0]?.score < e?.teams[1]?.score
            }
            sx={{
              width: '5rem',
              height: '4rem',
              ml: '0.25rem',
              fontSize: '2.5rem',
            }}
          />
          <Box
            sx={{
              marginLeft: '0.75rem',
            }}
          >
            {e?.teams[1]?.data?.sets?.map((_, index) => {
              return (
                <Score
                  key={index}
                  score={e?.teams[1]?.data?.sets[index]}
                  opponentScore={e?.teams[0]?.data?.sets[index]}
                  isWinner={
                    e?.teams[1]?.data?.sets[index] >
                    e?.teams[0]?.data?.sets[index]
                  }
                  sx={{ width: '1.1rem', height: '1.5rem', mb: 1 }}
                />
              );
            })}
          </Box>
        </Box>
        {live && l.halfTime && (
          <Box
            sx={{
              marginTop: '0.75rem',
            }}
          >
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                textAlign: 'center',
                fontWeight: 800,
              }}
              variant="body2"
              component="span"
            >
              {l.halfTime?.data?.main_score_1}
            </Typography>
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                mr: 1,
                ml: 1,
                fontSize: ' 0.7rem',
                textAlign: 'center',
              }}
              variant="body2"
              component="span"
            >
              {t(T_KEYS.EVENT_HALFTIME)}
            </Typography>
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                textAlign: 'center',
                fontWeight: 800,
              }}
              variant="body2"
              component="span"
            >
              {l.halfTime?.data.main_score_2}
            </Typography>
          </Box>
        )}
        {/* TODO: Afficher les quart temps lorsque le basket sera géré dans la
        nouvelle version */}
        {/* {e?.status === 'finish' && (
          <Box
            sx={{
              marginTop: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                ml: 1,
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  mb: 0.5,
                  fontSize: ' 0.7rem',
                  textAlign: 'center',
                }}
                variant="body2"
                component="span"
              >
                QT1
              </Typography>
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 800,
                }}
                variant="body2"
                component="span"
              >
                24 - 16
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                ml: 1,
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  mb: 0.5,
                  fontSize: ' 0.7rem',
                  textAlign: 'center',
                }}
                variant="body2"
                component="span"
              >
                QT2
              </Typography>
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 800,
                }}
                variant="body2"
                component="span"
              >
                21 - 16
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                ml: 1,
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  mb: 0.5,
                  fontSize: ' 0.7rem',
                  textAlign: 'center',
                }}
                variant="body2"
                component="span"
              >
                QT3
              </Typography>
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 800,
                }}
                variant="body2"
                component="span"
              >
                29 - 17
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
                ml: 1,
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  mb: 0.5,
                  fontSize: ' 0.7rem',
                  textAlign: 'center',
                }}
                variant="body2"
                component="span"
              >
                QT4
              </Typography>
              <Typography
                sx={{
                  lineHeight: 1,
                  m: 0,
                  textAlign: 'center',
                  fontWeight: 800,
                }}
                variant="body2"
                component="span"
              >
                19 - 14
              </Typography>
            </Box>
          </Box>
        )} */}
        {(e?.teams[0]?.data?.shootout > 0 ||
          e?.teams[1]?.data?.shootout > 0) && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                fontWeight: 800,
                color: (theme) => theme.vars.palette.background.default,
              }}
              variant="body2"
              component="span"
            >
              {t(T_KEYS.ACTION_SHOOTOUT)}
            </Typography>
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                color: (theme) => theme.vars.palette.background.default,
              }}
            >
              <Score
                score={e?.teams[0]?.data?.shootout}
                opponentScore={event?.teams?.[1]?.score}
                isWinner={
                  e?.teams[0]?.data?.shootout > e?.teams[1]?.data?.shootout
                }
                sx={{ width: '1.5rem', height: '2rem', mr: '0.25rem' }}
              />
              -
              <Score
                score={e?.teams[1]?.data?.shootout}
                opponentScore={event?.teams?.[1]?.score}
                isWinner={
                  e?.teams[1]?.data?.shootout > e?.teams[0]?.data?.shootout
                }
                sx={{ width: '1.5rem', height: '2rem', ml: '0.25rem' }}
              />
            </Box>
          </Box>
        )}

        {e?.status === 'live' && (
          <Box
            sx={{
              background: '#e57373',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '0.3rem',
              paddingBottom: '0.3rem',
              marginTop: '0.75rem',
              borderRadius: '0.25rem',
              width: 'calc(100% - 1rem)',
            }}
          >
            <Typography
              sx={{
                lineHeight: 1,
                m: 0,
                fontWeight: 800,
                color: 'white',
                textTransform: 'uppercase',
              }}
              variant="body2"
              component="span"
              color="white"
            >
              {t(T_KEYS.EVENT_LIVE)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Stack
      spacing={1}
      css={css`
        padding: 0 1rem 1rem 1rem;
      `}
      {...rest}
    >
      <Stack
        direction="row"
        justifyContent="center"
        css={css`
          width: 100%;
        `}
      >
        <EventStatus status={e?.status} />
      </Stack>
      <Stack
        direction="row"
        css={css`
          width: 100%;
          align-items: center;
          justify-content: space-between;
        `}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flex: 0.5,
          }}
        >
          {live && (
            <Box
              sx={{
                marginRight: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Card
                actionType="red_card"
                count={e?.teams[0]?.data?.red_cards}
              />
              <Card
                actionType="yellow_card"
                count={e?.teams[0]?.data?.yellow_cards}
              />
              <Card
                actionType="blue_card"
                count={e?.teams[0]?.data?.blue_cards}
              />
              <Card
                actionType="white_card"
                count={e?.teams[0]?.data?.white_cards}
              />
              {e?.teams?.[0]?.data?.tries >= 0 && (
                <Badge
                  badgeContent={e?.teams?.[0]?.data?.tries}
                  color="primary"
                  sx={{ marginTop: 1 }}
                >
                  <Image
                    src="https://img.scorenco.com/front/actions/try.svg"
                    sx={{
                      width: '1.5rem',
                      borderRadius: '100%',
                      padding: '0.15rem',
                      background: 'white',
                    }}
                  />
                </Badge>
              )}
            </Box>
          )}
          <RouteLink
            href={{
              routeKey: 'Team',
              params: { teamId: String(e?.teams?.[0]?.team_id) },
            }}
            disabled={demo || !e?.teams?.[0]?.team_id}
          >
            <Box>
              <ClubLogo
                css={css`
                  height: 4rem;
                  object-position: left;
                  flex: 1;
                `}
                logo={e?.teams?.[0]?.logo}
                name={e?.teams?.[0]?.name_in_competition}
                shieldColor="home"
                width={200}
                height={200}
              />
              <Box sx={{ flex: 1, color: 'white' }}>
                <TeamName name={e?.teams?.[0]?.name_in_competition} />
              </Box>
            </Box>
          </RouteLink>
        </Box>

        {scoreboard}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flex: 0.5,
            justifyContent: 'flex-end',
          }}
        >
          <RouteLink
            href={{
              routeKey: 'Team',
              params: { teamId: String(e?.teams?.[1]?.team_id) },
            }}
            disabled={demo || !e?.teams?.[1]?.team_id}
          >
            <Box>
              <ClubLogo
                css={css`
                  height: 4rem;
                  object-position: right;
                  flex: 1;
                `}
                logo={e?.teams?.[1]?.logo}
                name={e?.teams?.[1]?.name_in_competition}
                shieldColor="away"
                width={200}
                height={200}
              />
              <Box sx={{ flex: 1, color: 'white' }}>
                <TeamName
                  name={e?.teams?.[1]?.name_in_competition}
                  css={css`
                    text-align: right;
                  `}
                />
              </Box>
            </Box>
          </RouteLink>
          {live && (
            <Box
              sx={{
                marginLeft: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Card
                actionType="red_card"
                count={e?.teams[1]?.data?.red_cards}
              />
              <Card
                actionType="yellow_card"
                count={e?.teams[1]?.data?.yellow_cards}
              />
              <Card
                actionType="blue_card"
                count={e?.teams[1]?.data?.blue_cards}
              />
              <Card
                actionType="white_card"
                count={e?.teams[1]?.data?.white_cards}
              />
              {e?.teams?.[1]?.data?.tries > 0 && (
                <Badge
                  badgeContent={e?.teams?.[1]?.data?.tries}
                  color="primary"
                  sx={{ marginTop: 1 }}
                >
                  <Image
                    src="https://img.scorenco.com/front/actions/try.svg"
                    sx={{
                      width: '1.5rem',
                      borderRadius: '100%',
                      padding: '0.15rem',
                      background: 'white',
                    }}
                  />
                </Badge>
              )}
            </Box>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};
